import React, { useMemo, useEffect, useCallback } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import {
  Grid,
  Typography,
  makeStyles,
  createStyles,
  Theme,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@material-ui/core';
import { DefaultButton, BasePage, colors, Footer } from 'design-system';
import { XCircle } from 'react-feather';
import { Quotation, ProductConfiguration } from 'data-layer-cr/dist/models';
import { NATURAL_PERSON } from 'data-layer-cr/dist/constants/customerTypes';
import {
  validLegalEntity,
  customerWithoutNulls,
  appliesAutoPxDiscount,
} from 'data-layer-cr/dist/functions/quotations';
import { useNavigate } from 'react-router-dom';

// Methods
import { requiredDocumentsForEmission } from '../../../functions/quotationMethods';

// Redux
import { RootState } from '../../../store/rootReducer';
import { setStepperEmission } from '../../../store/steppers/actions';

const inlineBlock = 'inline-block';
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    inlineRow: {
      display: inlineBlock,
    },
    backgroundGradient: {
      background: 'linear-gradient(180deg, #CEE8FF 10.19%, #FFFFFF 100%)',
      height: '100%',
      minHeight: `calc(100vh - 56px)`,
    },
    contentWrapper: {
      padding: 32,
    },
    documentElementContainer: {
      display: inlineBlock,
      width: '100%',
      [theme.breakpoints.up('md')]: {
        textAlign: 'center',
        width: 'auto',
      },
    },
    documentHeaderRow: {
      marginBottom: theme.spacing(1),
      display: inlineBlock,
    },
    buttonContainer: {
      width: 240,
      marginTop: theme.spacing(1),
    },
    imageElement: {
      marginTop: -theme.spacing(2),
      [theme.breakpoints.up('md')]: {
        marginTop: theme.spacing(1),
      },
    },
    goToLoginButtonContainer: {
      paddingRight: '10px',
      position: 'absolute',
      top: 100,
      left: '85%',
    },
    goToLoginButtonIcon: {
      width: '52px',
      height: '46px',
      [theme.breakpoints.down('sm')]: {
        width: '27px',
        height: '24px',
      },
    },
    autoPxLogo: {
      paddingLeft: '.5rem',
      borderLeft: '1px solid #A3A2A2',
    },
  }),
);

function makeRow(content: string): React.ReactNode {
  return (
    <Grid
      container
      alignItems="center"
      key={content}
      justifyContent="flex-start"
      style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
    >
      <Typography variant="caption">
        <img src="/images/small_blue_check.svg" alt="check" />
        &nbsp;&nbsp;
        {content}
      </Typography>
    </Grid>
  );
}

export const EmissionWelcome = ({
  quotation,
  onStartRequested,
  configuration,
  setStepperEmissionAction,
}: Props): JSX.Element => {
  const navigate = useNavigate();
  /** When displaying this page, always set the form offset to 0 */
  useEffect(() => {
    setStepperEmissionAction(0);
  }, [setStepperEmissionAction]);

  const classes = useStyles();

  const customerName = useMemo(() => {
    return quotation.customerType === NATURAL_PERSON
      ? customerWithoutNulls(quotation).firstName
      : validLegalEntity(quotation).name;
  }, [quotation]);

  const { documents, image } = requiredDocumentsForEmission(
    quotation,
    configuration,
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isFooterMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const rowJustification = useMemo(
    () => (isMobile ? 'flex-start' : 'center'),
    [isMobile],
  );

  /**
   * Navigates to the login screen
   */
  const onGoToLoginRequested = useCallback((): void => {
    navigate(`/login${window.location.search}`);
  }, [navigate]);

  return (
    <BasePage
      title="Emisión de Póliza"
      padding={0}
      rightActions={<XCircle color={colors.white} />}
      appBarPosition="fixed"
    >
      <div className={classes.backgroundGradient}>
        <div className={classes.contentWrapper}>
          <Grid container justifyContent="center">
            <div className={classes.goToLoginButtonContainer}>
              <IconButton
                data-testid="go-to-login-button"
                onClick={onGoToLoginRequested}
              >
                <img
                  className={classes.goToLoginButtonIcon}
                  id="foo"
                  src="/images/goto_login.svg"
                  alt="top-artifact"
                />
              </IconButton>
            </div>
            <Grid item xs={12} md={6} container spacing={2}>
              <Grid
                data-testid="logo-row"
                item
                xs={12}
                container
                justifyContent={rowJustification}
              >
                <img
                  src="/images/assa_logo_blue.svg"
                  alt="logo"
                  style={{ paddingRight: '.5rem' }}
                />
                {appliesAutoPxDiscount(quotation) && (
                  <img
                    src="/images/AutoPx.svg"
                    alt="logo-auto-px"
                    data-testid="logo-auto-px"
                    className={classes.autoPxLogo}
                  />
                )}
              </Grid>
              <Grid
                data-testid="name-row"
                item
                xs={12}
                container
                justifyContent={rowJustification}
              >
                <Typography variant="h2" style={{ fontSize: 18 }}>
                  ¡Hola {customerName}!
                </Typography>
              </Grid>
              <Grid
                data-testid="call-to-action-row"
                item
                xs={12}
                container
                justifyContent={rowJustification}
              >
                <Typography variant="body2">
                  Obtener tu póliza de auto es fácil
                </Typography>
              </Grid>
              <Grid
                data-testid="documents-row"
                item
                xs={12}
                container
                justifyContent={rowJustification}
              >
                <div className={classes.inlineRow}>
                  <Grid container spacing={1}>
                    <div className={classes.documentElementContainer}>
                      <Typography
                        variant="caption"
                        gutterBottom
                        className={classes.documentHeaderRow}
                        align="center"
                      >
                        Necesitarás tener a mano:
                      </Typography>
                      {documents.map(
                        (element): React.ReactNode => makeRow(element),
                      )}
                    </div>
                  </Grid>
                </div>
              </Grid>
              <Grid item xs={12} container justifyContent="center">
                <img
                  className={classes.imageElement}
                  src={`/images/${image}`}
                  alt="art"
                  style={{ marginTop: 8 }}
                />
              </Grid>
              <Grid item xs={12} container justifyContent="center">
                <div className={classes.buttonContainer}>
                  <DefaultButton
                    id="sales-web-inspection-button-startemission"
                    data-testid="start-button"
                    fullWidth
                    onClick={onStartRequested}
                  >
                    Comencemos
                  </DefaultButton>
                </div>
              </Grid>
            </Grid>
          </Grid>
        </div>
        <Footer
          IsMobile={isFooterMobile}
          country="Costa Rica"
          linkText="Consulte nuestros términos y condiciones"
          linkHref="https://www.assanet.cr/condiciones-generales/#seguros-generales"
        />
      </div>
    </BasePage>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  quotation: Quotation;
  configuration: ProductConfiguration;
} => ({
  quotation: state.quotation.quotation,
  configuration: state.coverageCatalog.productConfig,
});

const reduxActions = {
  setStepperEmissionAction: setStepperEmission,
};

const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;
interface Props extends PropsFromRedux {
  onStartRequested: () => void;
}

export default connector(EmissionWelcome);
